<script lang="ts" setup>
import { computed, watch, ref, nextTick, provide } from 'vue';
import { useUboGet } from '@services/crm-public/orval-api/ubo/ubo';
import { type CrmDomainDomainUbosUboContext as UboContext } from '@services/crm-public/orval-api/model/crmDomainDomainUbosUboContext';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import { VButton } from '@shared/component/VButton';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { hasPermission } from '@permission/hasPermission';
import UboFields from '@relatie-lib/ubo/UboFields.vue';
import type {
  ParticuliereRelatie,
  Relatie,
  ZakelijkeRelatie,
} from '@relatie-lib/relatieGegevens/types';
import { useQueryClient } from '@tanstack/vue-query';
import { deepClone } from '@tools/helpers';
import VCallToAction from '@shared/component/VCallToAction/VCallToAction.vue';
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';
import { uboContextInjectionKey } from '@relatie-lib/ubo/keys';
import { type Ubo, type UboMetCheckResultaat } from './types';

const { relatie, relatieId, readonly, context, autoSave, prefillUbo, shouldConfirmRemoval } =
  defineProps<{
    relatieId: string;
    readonly?: boolean;
    toonUboCheckResultaten?: boolean;
    relatie?: Relatie;
    prefillUbo?: boolean;
    autoSave?: boolean;
    context: UboContext;
    shouldConfirmRemoval?: boolean;
  }>();

provide(uboContextInjectionKey, { value: context });

const magBewerken = hasPermission('Crm.UboBijwerken');
const magVerwijderen = hasPermission('Crm.UboVerwijderen');

const kanBijwerken = computed(() => magBewerken && !readonly);
const kanVerwijderen = computed(() => magVerwijderen && !readonly);

const uboFieldsRefs = ref<InstanceType<typeof UboFields>[]>([]);

const {
  data: ubos,
  error: uboError,
  isPending,
  queryKey,
} = useUboGet(
  computed(() => relatieId),
  {
    query: { select: (e) => e.result },
  },
);
useWatchErrorHandler(uboError);

function createEmptyUbo(): Ubo {
  return deepClone({
    rechtsvorm: 'N',
    achternaam: '',
    adres: { land: 'Nederland', postcode: null as any, isNederlands: true },
    id: '',
    inEditMode: true,
  });
}

const personen = ref<Array<Ubo> | Array<UboMetCheckResultaat>>(readonly ? [] : [createEmptyUbo()]);

async function toevoegen() {
  personen.value.push(createEmptyUbo());
  await nextTick(() => {
    window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
  });
}

function isZakelijk(
  rel: ZakelijkeRelatie | ParticuliereRelatie | undefined,
): rel is ZakelijkeRelatie {
  return (rel as ZakelijkeRelatie)?.typeName === 'Z';
}

const client = useQueryClient();

async function submitAll(): Promise<boolean> {
  const submissions = uboFieldsRefs.value.map((field) => {
    if (field && typeof field.onSubmit === 'function') {
      return field.onSubmit();
    }
    return true;
  });
  const results = await Promise.all(submissions);
  return results.every((result) => result === true);
}

defineExpose({ submitAll });

watch(
  () => ubos.value,
  (_ubos) => {
    const relatieHasNoUbos = !_ubos || _ubos.length < 1;

    if (
      prefillUbo &&
      relatieHasNoUbos &&
      isZakelijk(relatie) &&
      relatie.rechtsvorm === 'E' &&
      relatie.contactpersoon
    ) {
      personen.value = [createFromRechtspersoon(relatie)];
      return;
    }

    if (relatieHasNoUbos) return;

    personen.value = _ubos.map((u) => {
      const ubo: Ubo | UboMetCheckResultaat = {
        ...u,
        geboortedatum: new Date(u.geboortedatum),
        adres: {
          ...u.adres,
          postcode: u.adres.postcode ?? undefined,
          land: u.adres.land ?? undefined,
          isNederlands: u.adres.land === 'Nederland',
          adres: u.adres.adres ?? undefined,
          straat: u.adres.straat ?? undefined,
          huisnummer: u.adres.huisnummer ?? undefined,
          huisnummertoevoeging: u.adres.huisnummertoevoeging ?? undefined,
          plaats: u.adres.plaats ?? undefined,
        },
        inEditMode: false,
      };
      return ubo;
    });
  },
  { deep: true, immediate: true },
);
function refresh(idx: number) {
  personen.value[idx].inEditMode = false;
  client.invalidateQueries({ queryKey });
}

const kanBewerken = computed(
  () => kanBijwerken.value && personen.value.filter((f) => f.inEditMode).length === 0,
);

function createFromRechtspersoon(rel: ZakelijkeRelatie): Ubo {
  return {
    id: '',
    voorletters: rel.contactpersoon?.voorletters ?? undefined,
    achternaam: rel.contactpersoon?.naam ?? '',
    geboortedatum: rel.contactpersoon?.geboortedatum ?? undefined,
    adres: {
      postcode: rel.adres?.postcode ?? undefined,
      huisnummer: rel.adres?.huisnummer ?? undefined,
      straat: rel.adres?.straat ?? undefined,
      huisnummertoevoeging: rel.adres?.huisnummertoevoeging ?? undefined,
      plaats: rel.adres?.plaats ?? undefined,
      land: 'Nederland',
      isNederlands: true,
    },
    inEditMode: true,
  };
}

function isExistingUbo(persoon: Ubo): boolean {
  return !!persoon.id;
}

function isVerwijderbaar(persoon: Ubo): boolean {
  if (personen.value.length <= 1) return false;

  const validUboCount = personen.value.filter(isExistingUbo).length;

  return validUboCount > 1 || !isExistingUbo(persoon);
}
</script>
<template>
  <div class="flex flex-col gap-4">
    <VSkeleton height="lg" :count="5" v-if="isPending" />
    <VContentBox v-else-if="personen.length === 0">
      <VCallToAction
        class="py-16"
        icon="far fa-user-group"
        title="Er zijn geen UBO's bekend bij deze relatie."
      />
    </VContentBox>
    <div v-else class="flex flex-col gap-4">
      <div v-for="(persoon, idx) in personen" :key="persoon.id">
        <UboFields
          :auto-save="autoSave"
          ref="uboFieldsRefs"
          v-model="personen[idx]"
          :kan-bewerken="kanBewerken || persoon.inEditMode"
          :kan-verwijderen="kanVerwijderen && isVerwijderbaar(persoon)"
          :index="idx + 1"
          :relatie-id
          @removed="personen.splice(idx, 1)"
          @changed="refresh(idx)"
          :should-confirm-removal
        />
      </div>
    </div>
    <div class="flex justify-end" v-if="!readonly">
      <VSkeleton class="max-w-56" height="lg" v-if="isPending" />
      <VButton
        inverted
        prepend-icon="far fa-plus"
        @click="toevoegen()"
        :disabled="!autoSave && !kanBewerken"
      >
        Persoon toevoegen
      </VButton>
    </div>
  </div>
</template>
